import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncLoadScript } from "../../../utils/common";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { zPad } from "../../../utils/number";
import { CAMERA_CAPTURE_MODES } from "../constants";
import AppVideoElement from "./videoElement";
import svgIcons from "../../../assets/svg/svgIcons";

type StorageVideoViewProps = {
  cameraSerialNumber: string;
  onPlayCallback: (response: any) => void;
  onPauseCallback: () => void;
  onStopCallback: () => void;
  onTimeUpdate?: (currentTimestamp: number, remainingSeconds: number) => void;
  onNextCamera?: () => void;
  nextCameraLabel?: string;
};

const debugComponent = true;

export enum PlaybackState {
  PLAYING = "playing",
  PAUSED = "paused",
  STOPPED = "stopped",
}

const StorageVideoView: FC<StorageVideoViewProps> = ({
  cameraSerialNumber,
  onPlayCallback,
  onPauseCallback,
  onStopCallback,
  onTimeUpdate,
  onNextCamera,
  nextCameraLabel,
}) => {
  const { t } = useTranslation();

    const { getLocalStorageItem } = useLocalStorage();
    const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
      (state: any) => state.commonReducer.user
    );
    const loggedInUser: TLegacyUserWithAccount =
      loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
    const liveviewDurationSeconds =
      loggedInUser?.account?.cameraliveviewduration ?? 30;

  const initRef = React.useRef(false);
  const [progressValue, setProgressValue] = React.useState<number>(0);
  const [liveviewError, setLiveviewError] = React.useState<string | null>(null);
  const [sessionStable, setSessionStable] = React.useState(false);
  const playbackStateRef = React.useRef<PlaybackState>(PlaybackState.STOPPED);
  const setPlaybackState = (state: PlaybackState) => {
    playbackStateRef.current = state;
  };
  const playbackState = playbackStateRef.current;
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
  const timerIntervalRef = React.useRef<any>(null);
  const lastTimerSecondsRef = useRef<number>(0);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isFullScreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const playbackModalRef = useRef<TModalsState["showModalVideoPlayback"]>(null);
  const prevPlaybackTargetRef = useRef<string | null>(null);

  useEffect(() => {
    clearTimerInterval();
    // Suppress the vmRTC SDK race-condition rejection that fires when it tries to
    // send an SDP offer before the signaling WebSocket is ready. The SDK retries
    // successfully; this only prevents the CRA dev overlay from appearing.
    const vmRTCRejectionHandler = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes("signaling service")) {
        event.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", vmRTCRejectionHandler);
    return () => {
      window.removeEventListener("unhandledrejection", vmRTCRejectionHandler);
    };
  }, []);

  const clearTimerInterval = () => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log("== [clearTimerInterval] timer interval cleared");
    window.clearInterval(timerIntervalRef.current);
  };

  const intervalCallback = React.useCallback(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("_");
    if (sessionStable) {
      setTimerSeconds((time) => {
        const next = (time ?? 0) - 1;
        if (onTimeUpdate && playbackModalRef.current) {
          const { startTimestamp, duration } = playbackModalRef.current;
          const elapsed = (duration ?? 0) - next;
          onTimeUpdate(startTimestamp + elapsed, next);
        }
        return next;
      });
    }
  }, [sessionStable, onTimeUpdate]);

  const initTimerInterval = (totalTime: number) => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log(
        "== [initTimerInterval] timer started",
        timerIntervalRef.current
      );
    setTimerSeconds(totalTime);
    timerIntervalRef.current = window.setInterval(intervalCallback, 1000);
  };

  const resetInterval = useCallback(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [resetInterval] timer reset");
    clearTimerInterval();
    setTimerSeconds(null);
  }, []);

  const resumeInterval = () => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [resumeInterval] timer resumed");
    timerIntervalRef.current = window.setInterval(intervalCallback, 1000);
  };

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  playbackModalRef.current = modalsState.showModalVideoPlayback;

  const webRTCConfig = {
    // "staging" | "prod"
    env: loggedInUser?.config?.VISIONMAX_BASE_URL?.includes("staging")
      ? "staging"
      : "prod",
    // VM_ENV === "prods"
    //   ? process.env.REACT_APP_VISIONMAX_ENV
    //   : process.env.REACT_APP_VISIONMAX_ENV_STAGING,
    apiKey: loggedInUser?.config?.VISIONMAX_X_API_KEY,
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_KEY
    //   : process.env.REACT_APP_VISIONMAX_KEY_STAGING,
    clientAccessToken: localStorage.getItem(
      localStorageKeys.camerasAdminVMToken
    ),
    feature: {
      enableDefaultCameraSwitchButton: false,
    },
  };

  useEffect(() => {
    if (!sessionStable || progressValue !== 100) return;
    const vmRTC = (window as any).vmRTC;
    if (
      vmRTC &&
      modalsState.showModalVideoPlayback?.action === "play" &&
      playbackState === PlaybackState.STOPPED &&
      progressValue === 100 &&
      sessionStable
    ) {
      playClip();
      setLiveviewError(null);
    }
  }, [
    modalsState.showModalVideoPlayback?.captureMode,
    modalsState.showModalVideoPlayback?.duration,
    modalsState.showModalVideoPlayback?.startTimestamp,
    modalsState.showModalVideoPlayback?.action,
    sessionStable,
    progressValue,
  ]);

  const connectRTC = () => {
    if (initRef.current) return;

    // update the ref to ensure the code executes only once
    initRef.current = true;

    // initialize scripts start here
    if (APP_CONFIG.DEBUG.ALL) console.log("== [initLiveview]");

    // import the webRTC script from `public/js` directory
    // ---------------------------------------------------
    // other dependencies (aws-sdk-2.1490.0.min.js, mqttws31.js and adapter.js)
    // are already loaded in the index.html file
    // a copy of each of these files is archived in `src/components/visionmax-liveview-ui/archive/` directory
    asyncLoadScript(
      `${process.env.PUBLIC_URL}/js/vmRTC-umd.js`,
      "visionmax-liveview-webrtc",
      () => {
        if (APP_CONFIG.DEBUG.ALL)
          console.log("== [initLiveview] webrtc script loaded");
        try {
          // initialize the webRTC
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            // bind event handlers
            vmRTC.addEventListener("progressStatusUpdated", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [progressStatusUpdated]", input);
              setLiveviewError(null);
              setProgressValue(input);
            });
            //to catch event of connection fail
            vmRTC.addEventListener("connect fail", (input: any) => {
              vmRTC.stop();
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [connect fail]", input);
              setLiveviewError("connect fail");
              resetInterval();
              onStopCallback?.();
            });
            vmRTC.addEventListener("session stable", (input: any) => {
              (window as any).liveviewSessionStable = true;
              if (!!(window as any)[`liveviewAttempt_${cameraSerialNumber}`]) {
                delete (window as any)[`liveviewAttempt_${cameraSerialNumber}`];
              }
              setSessionStable(true);
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [session stable]", input);
            });
            //to catch event of connection lost
            vmRTC.addEventListener("connection lost", () => {
              vmRTC.stop();
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [connection lost]");
              setLiveviewError("connection lost");
              resetInterval();
              onStopCallback?.();
            });

            vmRTC.addEventListener("initial.fail", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [initial.fail]", input);
              setLiveviewError("initial fail");
              resetInterval();
              onStopCallback?.();
            });

            vmRTC.addEventListener("broker disconnnected", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [broker disconnnected]", input);
              // connection was, probably, unsuccessful after a wakeup
              if (
                !(window as any).liveviewSessionStable &&
                (window as any)[`liveviewAttempt_${cameraSerialNumber}`] <
                  APP_CONFIG.CAMERA_ADMIN.LIVEVIEW_RETRIES
              ) {
                resetInterval();
                onStopCallback?.();
              }
            });

            // setup and start
            if (APP_CONFIG.DEBUG.ALL || debugComponent)
              console.log("== [SERIAL_NUMBER]", cameraSerialNumber);
            vmRTC.setup(webRTCConfig).playback(cameraSerialNumber);
            (window as any)[`liveviewAttempt_${cameraSerialNumber}`] = !!(
              window as any
            )[`liveviewAttempt_${cameraSerialNumber}`]
              ? (window as any)[`liveviewAttempt_${cameraSerialNumber}`] + 1
              : 1;
          } else {
            console.error("== [initLiveview] vmRTC is not defined");
          }
        } catch (e) {
          if (APP_CONFIG.DEBUG.ALL) console.error("== ERR", e);
        }
      }
    );
  };

  const terminateRTC = useCallback(() => {
    const vmRTC = (window as any).vmRTC;
    if (vmRTC && typeof vmRTC.stop === "function") {
      try {
        vmRTC.stop(); // Stop the RTC connection
        initRef.current = false;
        console.log("[vmRTC] Connection stopped.");
      } catch (e) {
        console.error("[vmRTC] Error during stop:", e);
      }
    }
    // Reset everything related to the playback state
    resetInterval();
    setSessionStable(false);
    setProgressValue(0);
    setPlaybackState(PlaybackState.STOPPED);
    onStopCallback?.(); // Call the onStop callback
  }, [dispatch, onStopCallback, resetInterval]);

  const playClip = useCallback(() => {
    if (!modalsState.showModalVideoPlayback) return;
    const vmRTC = (window as any).vmRTC;
    if (!vmRTC) {
      console.error("vmRTC is not initialized");
      setIsSwitching(false);
      return;
    }
    const captureMode = modalsState.showModalVideoPlayback.captureMode;
    const payload = {
      start: (modalsState.showModalVideoPlayback.startTimestamp ?? 0).toString(),
      end: (
        (modalsState.showModalVideoPlayback.startTimestamp ?? 0) +
        (modalsState.showModalVideoPlayback.duration ?? 0)
      ).toString(),
      capture_mode: captureMode,
      type: modalsState.showModalVideoPlayback.storageType ?? "SDCARD",
    };
    vmRTC.playVideoClips(payload, (res: any) => {
      if (res?.error) {
        toast.error(res.error);
        setIsSwitching(false);
        resetInterval();
        setPlaybackState(PlaybackState.STOPPED);
        onStopCallback?.();
        return;
      }
      setIsSwitching(false);
      setPlaybackState(PlaybackState.PLAYING);
      if (timerIntervalRef.current) {
        resetInterval();
      }
      initTimerInterval(
        modalsState?.showModalVideoPlayback?.duration ??
          liveviewDurationSeconds
      );
      onPlayCallback?.(res);
    });
  }, [initTimerInterval, modalsState.showModalVideoPlayback, onPlayCallback, liveviewDurationSeconds]);

  useEffect(() => {
    if (modalsState.showModalVideoPlayback && cameraSerialNumber) {
      if (modalsState.showModalVideoPlayback.action === "play") {
        const captureMode = modalsState.showModalVideoPlayback.captureMode;
        const storageType = modalsState.showModalVideoPlayback.storageType;
        const playbackTarget = `${captureMode}::${storageType ?? ""}`;
        const cameraSwitched = prevPlaybackTargetRef.current !== null && prevPlaybackTargetRef.current !== playbackTarget;
        prevPlaybackTargetRef.current = playbackTarget;

        if (!sessionStable) {
          connectRTC();
        } else if (
          cameraSwitched &&
          (playbackState === PlaybackState.PLAYING ||
            playbackState === PlaybackState.PAUSED)
        ) {
          setIsSwitching(true);
          resetInterval();
          playClip();
        } else if (playbackState === PlaybackState.PAUSED) {
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            vmRTC.resumePlay({}, (res: any) => {
              if (APP_CONFIG.DEBUG.ALL) console.log("resumed");
            });
            setPlaybackState(PlaybackState.PLAYING);
            resumeInterval();
            onPlayCallback?.({});
          }
        } else if (playbackState !== PlaybackState.PLAYING) {
          playClip();
          initTimerInterval(
            modalsState.showModalVideoPlayback.duration ??
              liveviewDurationSeconds
          );
        }
      } else if (modalsState.showModalVideoPlayback.action === "pause") {
        const vmRTC = (window as any).vmRTC;
        if (vmRTC) {
          vmRTC.pausePlay({}, (res: any) => {
            if (APP_CONFIG.DEBUG.ALL) console.log("paused successfully");
          });
          onPauseCallback?.();
          clearTimerInterval();
          setPlaybackState(PlaybackState.PAUSED);
        }
      }
    } else {
      if (sessionStable) {
        terminateRTC();
      }
    }
  }, [cameraSerialNumber, modalsState.showModalVideoPlayback, playbackState, liveviewDurationSeconds]);

  useEffect(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [timerSeconds]", timerSeconds);
    if (timerSeconds !== null && timerSeconds <= 0 && sessionStable) {
      // terminateRTC();
      dispatch(setModalsData({ ...modalsState, showModalVideoPlayback: null }));
    }
  }, [timerSeconds]);

  const TimerDisplayComponent = useCallback(() => {
    const display = timerSeconds !== null ? timerSeconds : lastTimerSecondsRef.current;
    if (timerSeconds !== null) lastTimerSecondsRef.current = timerSeconds;
    return (
      <div className="absolute right-4 top-4 font-bold text-white/70 text-lg z-40">
        {`${Math.floor(display / 60)}:${zPad(display % 60, 2)}`}
      </div>
    );
  }, [timerSeconds]);

  // Effect to observe full screen changes
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const handleFullscreen = () => {
    if (containerRef?.current) {
      if (isFullScreen) {
        document.exitFullscreen();
      } else {
        containerRef.current?.requestFullscreen();
      }
    }
  };

  if (modalsState.showModalVideoPlayback === null) return null;

  return (
    <div
      id="visionmax-playback"
      className="w-full h-full flex justify-center items-center relative z-0"
      style={{ width: "100%", height: "100%" }}
      ref={containerRef}
    >
      <TimerDisplayComponent />
      {isSwitching && (
        <div className="absolute inset-0 flex items-center justify-center z-20 rounded-xl bg-black/30">
          <img src={svgIcons.LoadingAnimation} alt="Switching camera" className="size-10" />
        </div>
      )}
      {/* Centered progress bar or error message display */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        {!!liveviewError ? (
          <p className="text-lg uppercase font-bold text-gray-200/70 text-center self-center">
            {liveviewError}
          </p>
        ) : progressValue < 100 ? (
          <div
            className="flex items-center justify-center self-center gap-4"
            role="progressbar"
            aria-label={`loading progress`}
            aria-valuemin={0}
            aria-valuenow={progressValue}
            aria-valuemax={100}
          >
            <span className="text-gray-200/70">{progressValue}%</span>
          </div>
        ) : null}
      </div>
      {/* Video element */}
      <AppVideoElement
        id="rtc_liveview"
        autoPlay
        controls={false}
        fixedHeightClass={isFullScreen ? "h-full" : "h-64"}
        wrapperClassName="absolute inset-0 w-full h-full object-cover rounded-xl z-5"
        playsInline
      />
      {/* Fullscreen Button (overlay) */}
      <button
        onClick={handleFullscreen}
        className="absolute bottom-4 right-4 px-3 py-2 bg-white/70 rounded-full shadow hover:bg-white z-10"
      >
        ⛶
      </button>
      {/* Next Camera Button — only needed here while fullscreen, since the
          fullscreen API only renders this container's subtree, cutting off
          the equivalent button rendered by the parent outside it */}
      {isFullScreen && onNextCamera && nextCameraLabel && (
        <button
          onClick={onNextCamera}
          className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/70 hover:bg-white text-black text-sm font-medium rounded-full shadow z-10"
        >
          Next: {nextCameraLabel}
        </button>
      )}
    </div>
  );
};
export default StorageVideoView;
